import { isEqual } from 'ohash'
import type { LocationQueryRaw } from 'vue-router'
import type FilterBar from '~/components/filter-bar/FilterBar.vue'

type Options = {
  sortOrderParameter: string
  defaultSortOrder: string
  orderByOptions: Record<string, string>
  pageParameter: string
  filterBar: Ref<InstanceType<typeof FilterBar> | null>
  scrollToTopElement: Ref<Element | null>
}

export const useHallRefinements = (options: Options) => {
  const route = useRoute()

  const requestedPageIndex = usePageNumberParameter(options.pageParameter)

  const sortOrderParameter = computed(() => {
    const parameterValue = route.query[options.sortOrderParameter]

    if (typeof parameterValue === 'string') {
      if (options.orderByOptions[parameterValue]) {
        return parameterValue
      }
    }

    return null
  })

  const effectiveOrderBy = computed(() => sortOrderParameter.value ?? options.defaultSortOrder)

  interface Choices {
    choices: number[]
  }

  interface Range {
    min: number
    max: number
  }

  type Filter = Choices | Range

  const filters = computed<Record<string, Filter>>((previous) => {
    const query = route.query

    const appliedFilters: Record<string, Filter> = {}
    Object.keys(query).forEach((param) => {
      const match = param.match(/^f(\d+)$/)
      if (!match) {
        return
      }

      if (query[param]) {
        let filter: Filter | null = null
        if (Array.isArray(query[param])) {
          filter = {
            choices: query[param]
              .filter((x) => typeof x === 'string')
              .map((x) => parseInt(x, 10))
              .filter((x) => x > 0),
          }
        } else if (query[param].includes(',')) {
          const str = query[param].split(',')
          const min = parseInt(str[0] as string, 10)
          const max = parseInt(str[1] as string, 10)

          if (min <= max) {
            filter = {
              min,
              max,
            }
          }
        } else {
          filter = {
            choices: [parseInt(query[param], 10)],
          }
        }

        if (filter) {
          appliedFilters[match[1]!] = filter
        }
      }
    })

    // Avoid updating reference if functionally identical
    if (previous && isEqual(previous, appliedFilters)) {
      return previous
    }

    return appliedFilters
  })

  const filterParameters = computed(() => {
    return Object.fromEntries(
      Object.entries(filters.value).map(([key, value]): [string, string | string[]] => {
        if ('choices' in value) {
          return [`f${key}`, value.choices.map((x) => x.toString())]
        }

        return [`f${key}`, `${value.min},${value.max}`]
      })
    )
  })

  const navigate = (queryModifications: LocationQueryRaw) => {
    return navigateTo({
      query: {
        ...route.query,
        ...queryModifications,
        [options.pageParameter]: undefined,
      },
      ...(options.scrollToTopElement.value
        ? {
            options: {
              scroll: options.filterBar.value?.hasScrolled ? { el: options.scrollToTopElement.value } : false,
            },
          }
        : {}),
    })
  }

  const setOrderBy = (newValue: string) => {
    if (options.orderByOptions[newValue]) {
      navigate({
        [options.sortOrderParameter]: newValue === options.defaultSortOrder ? undefined : newValue,
      })
    }
  }

  type FilterRemoveCandidate = ChoiceRemoveCandidate | RangeRemoveCandidate

  interface ChoiceRemoveCandidate {
    type: 'choice'
    group: number
    id: number
  }

  interface RangeRemoveCandidate {
    type: 'range'
    group: number
  }

  const removeFilter = (candidate: FilterRemoveCandidate) => {
    if (candidate.type === 'choice') {
      const choices = filterParameters.value[`f${candidate.group}`]

      if (Array.isArray(choices)) {
        const newParameter = choices.filter((v) => v !== candidate.id.toString())

        navigate({
          [`f${candidate.group}`]: newParameter.length ? newParameter : undefined,
        })
      }
    }

    if (candidate.type === 'range') {
      navigate({
        [`f${candidate.group}`]: undefined,
      })
    }
  }

  const clearFilters = () => {
    navigate(Object.fromEntries(Object.keys(filterParameters.value).map((key) => [key, undefined])))
  }

  const updateChoices = (group: number, choices: number[]) => {
    navigate({
      [`f${group}`]: choices.length ? choices : undefined,
    })
  }

  const updateRange = (group: number, min: number, max: number) => {
    navigate({
      [`f${group}`]: `${min},${max}`,
    })
  }

  return {
    filters,
    filterParameters,
    currentPage: requestedPageIndex,
    setOrderBy,
    effectiveOrderBy,
    removeFilter,
    clearFilters,
    updateChoices,
    updateRange,
  }
}
